/**
 * FollowWriterButton — Substack-style "Follow"/"Following" toggle.
 * Requires a signed-in reader; when anonymous, we route them to
 * /auth?next=<current-url> so they land back on the article after
 * signing in.
 */
import { useCallback, useEffect, useState } from "react";
import { UserPlus, Check, Loader2 } from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;

function token() {
  return window.localStorage.getItem("sp_token") || window.localStorage.getItem("token") || "";
}

async function api(path, opts = {}) {
  const r = await fetch(`${BASE}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token()}`,
      ...(opts.headers || {}),
    },
  });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}

export default function FollowWriterButton({
  writerId,
  size = "md",
  className = "",
  testIdPrefix = "follow-writer",
}) {
  const [state, setState] = useState({ is_following: false, follower_count: 0, loading: true });

  const load = useCallback(async () => {
    if (!writerId) return;
    try {
      const d = await api(`/writers/${writerId}/follow-state`);
      setState({ is_following: d.is_following, follower_count: d.follower_count, loading: false });
    } catch {
      setState((s) => ({ ...s, loading: false }));
    }
  }, [writerId]);

  useEffect(() => { load(); }, [load]);

  const toggle = async () => {
    if (!token()) {
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/auth?next=${next}`;
      return;
    }
    setState((s) => ({ ...s, loading: true }));
    try {
      const method = state.is_following ? "DELETE" : "POST";
      const d = await api(`/writers/${writerId}/follow`, { method });
      setState({ is_following: d.is_following, follower_count: d.follower_count, loading: false });
    } catch (e) {
      setState((s) => ({ ...s, loading: false }));
    }
  };

  const sizes = {
    sm: "px-3 py-1 text-xs",
    md: "px-4 py-1.5 text-sm",
    lg: "px-5 py-2.5 text-base",
  }[size] || "px-4 py-1.5 text-sm";

  return (
    <div className="inline-flex items-center gap-2" data-testid={testIdPrefix}>
      <button
        type="button"
        onClick={toggle}
        disabled={state.loading}
        className={`inline-flex items-center gap-1.5 rounded-full font-semibold transition-colors ${sizes} ${
          state.is_following
            ? "bg-slate-100 text-slate-800 border border-slate-300 hover:bg-slate-200"
            : "bg-teal-700 text-white hover:bg-teal-800"
        } ${state.loading ? "opacity-70 cursor-wait" : ""} ${className}`}
        data-testid={`${testIdPrefix}-toggle`}
      >
        {state.loading ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : state.is_following ? (
          <Check className="w-3.5 h-3.5" />
        ) : (
          <UserPlus className="w-3.5 h-3.5" />
        )}
        {state.is_following ? "Following" : "Follow"}
      </button>
      {state.follower_count > 0 && (
        <span className="text-xs text-slate-500" data-testid={`${testIdPrefix}-count`}>
          {state.follower_count.toLocaleString()} follower{state.follower_count === 1 ? "" : "s"}
        </span>
      )}
    </div>
  );
}
